import dayjs from 'dayjs';
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { useTabManager } from './tab-manager-context';

import {
  AnalyzeSavedView,
  GroupTooltipData,
  SearchTimeRangeData,
  Tag,
  TimeRanges,
  ViewType,
} from '@controlrooms/models';
import { ParentTag } from '@controlrooms/models';

export interface AssessSystemTags {
  [folder: number]: Tag[];
}
export interface AnalyzeContextProps {
  updateView: (view: AnalyzeSavedView) => void;
  selectedFolders: number[];
  selectedViewTitle: string;
  groupTooltipData?: GroupTooltipData;
  setGroupTooltipData: Dispatch<SetStateAction<GroupTooltipData | undefined>>;
  labelTooltipData?: GroupTooltipData;
  setLabelTooltipData: Dispatch<SetStateAction<GroupTooltipData | undefined>>;
  searchTimeRangeData?: SearchTimeRangeData;
  setSearchTimeRangeData: Dispatch<SetStateAction<SearchTimeRangeData | undefined>>;
  pinnedTags: ParentTag[];
  currentFolder: number | string | null;
  setCurrentFolder: Dispatch<SetStateAction<number | string | null>>;
  setSelectedViewTitle: Dispatch<SetStateAction<string>>;
  showHiddenTags: boolean;
  setShowHiddenTags: Dispatch<SetStateAction<boolean>>;
  visibleTags: AssessSystemTags;
  setVisibleTags: Dispatch<SetStateAction<AssessSystemTags>>;
}

const defaultState = {
  updateView: () => null,
  selectedFolders: [],
  selectedViewTitle: 'Default View',
  groupTooltipData: undefined,
  setGroupTooltipData: () => undefined,
  labelTooltipData: undefined,
  setLabelTooltipData: () => undefined,
  searchTimeRangeData: undefined,
  setSearchTimeRangeData: () => undefined,
  pinnedTags: [],
  currentFolder: null,
  setCurrentFolder: () => null,
  showHiddenTags: false,
  setShowHiddenTags: () => null,
  setSelectedViewTitle: () => null,
  visibleTags: {},
  setVisibleTags: () => null,
};

export const AnalyzeContext = createContext<AnalyzeContextProps>(defaultState);

const AnalyzeContextProvider: React.FC = ({ children }) => {
  const { activeViewState } = useTabManager();

  const [selectedViewTitle, setSelectedViewTitle] = useState<string>('');

  const [showHiddenTags, setShowHiddenTags] = useState<boolean>(false);
  const [currentFolder, setCurrentFolder] = useState<number | string | null>(null);

  //To capture all visible tags that are visble in Assess
  const [visibleTags, setVisibleTags] = useState<AssessSystemTags>({});

  const [groupTooltipData, setGroupTooltipData] = useState<GroupTooltipData>();
  const [labelTooltipData, setLabelTooltipData] = useState<GroupTooltipData>();
  const [searchTimeRangeData, setSearchTimeRangeData] = useState<SearchTimeRangeData>();

  const updateView = useCallback((view: AnalyzeSavedView) => {
    if (
      view.timeSelection.timeRange === TimeRanges.PRESET &&
      view.timeSelection.streamingTimeInSeconds
    ) {
      view.timeSelection.endTime = dayjs().toISOString();
      view.timeSelection.startTime = dayjs()
        .subtract(view.timeSelection.streamingTimeInSeconds, 'seconds')
        .toISOString();
    }
    setSelectedViewTitle(view.name || '');
  }, []);

  const analyzeState = useMemo(
    () => ({
      updateView,
      selectedFolders: activeViewState.view[ViewType.ANALYZE].selectedFolders,
      groupTooltipData,
      setGroupTooltipData,
      labelTooltipData,
      setLabelTooltipData,
      searchTimeRangeData,
      setSearchTimeRangeData,
      pinnedTags: activeViewState.view[ViewType.ANALYZE].pinnedTags as ParentTag[],
      currentFolder,
      setCurrentFolder,
      showHiddenTags,
      setShowHiddenTags,
      selectedViewTitle,
      setSelectedViewTitle,
      visibleTags,
      setVisibleTags,
    }),
    [
      currentFolder,
      groupTooltipData,
      labelTooltipData,
      searchTimeRangeData,
      updateView,
      showHiddenTags,
      selectedViewTitle,
      setSelectedViewTitle,
      activeViewState,
      visibleTags,
      setVisibleTags,
    ],
  );

  return <AnalyzeContext.Provider value={analyzeState}>{children}</AnalyzeContext.Provider>;
};

export default AnalyzeContextProvider;
